import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type TransformsElementProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { CheckboxInputDefinitionMapper } from './ai/checkbox-input.definition-mapper';
import { CheckboxInputElementHandler } from './ai/checkbox-input.element-handler';
import { CheckboxInputComponent } from './checkbox-input.component';
import { CheckboxInputPropsBuilder } from './checkbox-input.props-builder';
import { generateCheckboxInputFormField } from './generation/form-field';
import { checkboxInputV2Migration } from './migrations/002-checkbox-input.migration';
import { checkboxInputV3PaddingToMarginMigration } from './migrations/003-checkbox-input-padding-to-margin.migration';

export { transformToCheckboxInput } from './checkbox-input.transforms';
export type CheckboxInputElement = CanvasElementFrom<'checkboxInput'>;
export type CheckboxInputDef = WithProps<CheckboxInputPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    checkboxInput: CheckboxInputDef;
  }
}

const DEFAULT_WIDTH = 356;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed'];

registerElementDefinition({
  type: 'checkboxInput',
  componentType: CheckboxInputComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Checkbox',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: CheckboxInputPropsBuilder,
  generation: {
    formField: generateCheckboxInputFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      readonly: 'content.input.readonly',
    },
  },
  features: {
    ai: {
      optionalInput: true,
    },
  },
  ai: {
    name: 'checkbox',
    handler: () => CheckboxInputElementHandler,
    definitionMapper: () => CheckboxInputDefinitionMapper,
  },
  version: 3,
  migrations: {
    2: checkboxInputV2Migration,
    3: checkboxInputV3PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Value', dataType: 'Boolean' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          columns: [
            {
              columnType: 'Boolean',
              dataType: 'Boolean',
              name: '', // for non-object value schema, this should be empty
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'Is Checked',
        },
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  TransformsElementProps &
  VisibilityElementProps;

export type CheckboxInputPropsModel = PropsModel & {
  content: {
    title: ElementLabelPropsWithPositionAndWidth;
    input?: {
      readonly?: boolean;
    };
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    labelBlock: ElementLabelStyleProps;
    input: {
      symbol: PropertiesConfigInputColorModel;
      fill: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
  };
};

function getDefaults(): PropsOf<'checkboxInput'> {
  return {
    content: {
      title: {
        label: 'Checkbox',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight',
        fontStyle: [],
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      input: {
        symbol: { color1: '$t.accentLabel', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'checkboxInput'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('checkboxInput', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.input',
        'Checkbox',
        [epc.fields.toggleField('content.input.readonly', 'Read Only')],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Value', dataType: 'Boolean' }],
            },
          ],
        },
      ),
      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.input', 'Checkbox', [
          epc.fields.colorField('style.input.symbol', 'Symbol', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
              epc.fields.radiusField('style.input.radius'),
            ],
            'content.input.readonly',
            (value) => !!value,
          ),
        ]),

        epc.subsections.labelStyleSubsection({
          color: 'style.general.title',
          enabled: 'content.title.enabled',
          fontFamily: 'style.labelBlock.fontFamily',
          fontSize: 'style.labelBlock.fontSize',
          fontStyle: 'style.labelBlock.fontStyle',
          fontWeight: 'style.labelBlock.fontWeight',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.labelBlock.iconSize',
          lineHeight: 'style.labelBlock.lineHeight',
          position: 'content.title.position',
          width: 'content.title.width',
        }),
      ],
      true,
    ),
  ]);
}
